import React, {useRef, useCallback, useEffect} from "react";
import {GoogleMap, Marker, Circle, useJsApiLoader} from "@react-google-maps/api";
import styles from "./styles.module.scss";
import CircularProgressIndicator from "@/app/components/circular_progress_indicator";
import {ThirtyText} from "@/app/components/texts";
import Button from "@/app/components/button";
import {s} from "@/app/utils/s";
import {t} from "i18next";
import {UpdateUserLocationModel} from "@/app/api/user";

export interface MapWithRadiusProps {
  coords?: UpdateUserLocationModel;
  radiusKm: number;
  getCoords: () => void;
  error?: string;
}

const containerStyle = {
  width: "100%",
  height: "100%",
};

const mapOptions: google.maps.MapOptions = {
  disableDefaultUI: true,
  clickableIcons: false,
  gestureHandling: "greedy",
};

const MapWithRadius: React.FC<MapWithRadiusProps> = ({coords, radiusKm, getCoords, error}) => {
  const mapRef = useRef<google.maps.Map | null>(null);

  const {isLoaded, loadError} = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
  });

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const onCircleLoad = useCallback((circle: google.maps.Circle) => {
    if (mapRef.current) {
      mapRef.current.fitBounds(circle.getBounds()!);
    }
  }, []);

  // Move map camera when coordinates change
  useEffect(() => {
    if (mapRef.current && coords) {
      const newCenter = { lat: coords.latitude, lng: coords.longitude };
      mapRef.current.panTo(newCenter);
    }
  }, [coords]);


  if (loadError) return <div>Error loading map</div>;
  if (!isLoaded) return <CircularProgressIndicator/>;

  return (
    <div className={styles.mapContainer}>
      {error !== undefined && <div className={styles.errorBlock}>
          <ThirtyText text={error || t(s.allowGeolocation)}/>
          <Button text={t(s.tryAgain)} onClick={getCoords}/>
      </div>}
      {coords === undefined && <div className={styles.errorBlock}>
          <CircularProgressIndicator isGreen/>
      </div>}
      {error === undefined && coords !== undefined && <GoogleMap
          mapContainerStyle={containerStyle}
          onLoad={onMapLoad}
          options={mapOptions}
      >
          <Marker position={{lat: coords.latitude, lng: coords.longitude}} icon={"/map.svg"}/>
          <Circle
              center={{lat: coords.latitude, lng: coords.longitude}}
              radius={radiusKm * 1000}
              options={{
                fillColor: "#C497FF",
                fillOpacity: 0.5,
                strokeWeight: 0,
              }}
              onLoad={onCircleLoad}
          />
      </GoogleMap>}
    </div>
  );
};

export default MapWithRadius;